/**
 * Read-only dashboard API (brief §5 Phase 1).
 *
 * Node health and active calls arrive live over a WebSocket that pushes a
 * fresh payload on every state change; registrations, trunks, and E911
 * warnings are polled over REST. Every value is derived from the backend's
 * in-memory state, so a dead node degrades the view rather than blocking it.
 */

import { apiFetch, notifyUnauthenticated } from "./client";

export type NodeHealth = "unknown" | "online" | "degraded" | "offline";

/** One node's live snapshot (`NodeSnapshot.to_dict()`). */
export interface NodeSnapshot {
  node_id: number;
  name: string;
  health: NodeHealth;
  last_seen: string | null;
  last_error: string | null;
  active_call_count: number;
  registration_count: number;
  trunk_count: number;
  feed_connected: boolean;
}

/** Node-tagged rows whose remaining fields come straight from the node feed. */
export type NodeRow = { node_id: number; node: string } & Record<string, unknown>;
export type TrunkRow = NodeRow & { trunk_id: string };

/** An E911 IP spot-check warning (§4B). */
export interface E911Warning {
  device_id: number | null;
  mac: string | null;
  label: string | null;
  extension: string | null;
  location_id: number | null;
  location_name: string | null;
  registering_ip: string;
  kind: string;
  message: string;
}

/** The live push payload (WebSocket, and the `/nodes` + `/calls` fallback). */
export interface DashboardLive {
  nodes: NodeSnapshot[];
  calls: NodeRow[];
  online: number;
  total: number;
}

export function getNodes(): Promise<{ nodes: NodeSnapshot[]; online: number; total: number }> {
  return apiFetch("/api/dashboard/nodes");
}

export function getCalls(): Promise<{ calls: NodeRow[]; count: number }> {
  return apiFetch("/api/dashboard/calls");
}

export function getRegistrations(): Promise<{ registrations: NodeRow[]; count: number }> {
  return apiFetch("/api/dashboard/registrations");
}

export function getTrunks(): Promise<{ trunks: TrunkRow[]; count: number }> {
  return apiFetch("/api/dashboard/trunks");
}

export function getE911Warnings(): Promise<{ warnings: E911Warning[]; count: number }> {
  return apiFetch("/api/dashboard/e911-warnings");
}

/** Callbacks for the live dashboard WebSocket feed. */
export interface DashboardFeedHandlers {
  onLive: (data: DashboardLive) => void;
  onStatus?: (connected: boolean) => void;
}

/**
 * Open the live dashboard WebSocket feed, reconnecting with backoff.
 *
 * Same-origin, so the session cookie authenticates the upgrade (nginx fronts
 * it — brief §2.8). Returns a cleanup function that closes the socket and
 * cancels any pending reconnect.
 */
export function openDashboardFeed(handlers: DashboardFeedHandlers): () => void {
  let closed = false;
  let socket: WebSocket | null = null;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let backoff = 1000;

  const connect = (): void => {
    if (closed) return;
    const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(`${scheme}//${window.location.host}/api/dashboard/ws`);
    socket.onopen = () => {
      backoff = 1000;
      handlers.onStatus?.(true);
    };
    socket.onmessage = (event: MessageEvent) => {
      try {
        const frame = JSON.parse(String(event.data)) as DashboardLive | { type?: string };
        // Server liveness pings ride quiet stretches; not payload.
        if ((frame as { type?: string }).type === "heartbeat") return;
        handlers.onLive(frame as DashboardLive);
      } catch {
        // ignore malformed frames
      }
    };
    socket.onclose = (event: CloseEvent) => {
      handlers.onStatus?.(false);
      // 1008 = the server refused/revoked auth (session expired). Retrying
      // would loop silently forever — surface it as a sign-out instead.
      if (event?.code === 1008) {
        notifyUnauthenticated();
        return;
      }
      if (!closed) {
        retry = setTimeout(connect, backoff);
        backoff = Math.min(backoff * 2, 15000);
      }
    };
    socket.onerror = () => socket?.close();
  };

  connect();
  return () => {
    closed = true;
    if (retry) clearTimeout(retry);
    socket?.close();
  };
}
